// src/features/shared-scope/components/SharedLinksAdminPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Shared Links Admin — Unified Generator + Forensic Analytics Dashboard
//
// Features:
//   • Generate shared links for ANY scope type (TableOps, IoT, Generic)
//   • Tenant-aware: uses active tenant from sidebar (superadmin override)
//   • Viewer config: language, theme embedded in link metadata
//   • FF demo link discovery (prominent banner with URL + copy)
//   • Per-link stats + expandable visitor forensic details
//   • Revoke action per link
//
// PAC Glass design system — dark glassmorphism, transparent blur surfaces.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createSharedLink,
  listSharedLinks,
  revokeSharedLink,
  getDemoLink,
  getSharedLinkVisitors,
  type SharedLinkRecord,
  type SharedLinkVisitor,
  type ViewerConfig,
  type SharedAccessLevel,
} from '../api/sharedScopeApi';
import { useTenantOverride } from '@/shared/auth/tenantContext';
import {
  DEVICE_KEYS,
  type Device,
} from '../../system/devices/api/iotDevicesApi';
import { useDevicesList } from '../../system/devices/hooks/useDevicesList';
import ScopeSignalSelector, { useScopeSignalSelection, type ScopeSignalItem } from '@/shared/components/ui/ScopeSignalSelector';

// ── Query Keys ───────────────────────────────────────────────────────────

const KEYS = {
  list:     ['shared-links', 'all'] as const,
  demo:     ['shared-links', 'demo'] as const,
  visitors: (token: string) => ['shared-links', 'visitors', token] as const,
};

// ── Scope Type Presets ───────────────────────────────────────────────────

const SCOPE_PRESETS = [
  {
    value:    'table-ops',
    label:    '🎮 TableOps — Omnipotent Viewer',
    scope_ns: 'sim.tableops',
    description: 'Big Brother view of the restaurant simulation — tables, orders, sessions.',
  },
  {
    value:    'iot-devices',
    label:    '📡 IoT Devices — Telemetry Dashboard',
    scope_ns: 'iot',
    description: 'Live sensor data — temperature, humidity, RSSI, battery for selected devices.',
  },
  {
    value:    'daemon-ops',
    label:    '🖥️ DaemonOps — System Metrics',
    scope_ns: 'pacdaemon.stats',
    description: 'CPU, memory, disk, network, ping, temperature — PACDaemon edge agent telemetry.',
  },
  {
    value:    'generic',
    label:    '📦 Generic — Scope Entity Viewer',
    scope_ns: '',
    description: 'Raw scope entity viewer for any namespace and key.',
  },
] as const;

const LANGUAGE_OPTIONS = [
  { value: '',   label: 'Default (browser)' },
  { value: 'en', label: '🇬🇧 English' },
  { value: 'es', label: '🇪🇸 Español' },
  { value: 'fr', label: '🇫🇷 Français' },
  { value: 'de', label: '🇩🇪 Deutsch' },
  { value: 'pt', label: '🇧🇷 Português' },
  { value: 'it', label: '🇮🇹 Italiano' },
  { value: 'ja', label: '🇯🇵 日本語' },
  { value: 'zh', label: '🇨🇳 中文' },
];

const TTL_OPTIONS = [
  { label: 'No expiry',  value: 0 },
  { label: '1 hour',     value: 1 },
  { label: '6 hours',    value: 6 },
  { label: '24 hours',   value: 24 },
  { label: '7 days',     value: 168 },
  { label: '30 days',    value: 720 },
];

const PHANTOM_ROLES = [
  { value: '',                            label: 'None (anonymous viewer)' },
  { value: 'finalfantasy.demo',           label: '🎮 Demo — Viewer' },
  { value: 'finalfantasy.superadmin',     label: '👑 Super Admin' },
  { value: 'finalfantasy.bot.waitress',   label: '🤖 Bot — Waitress' },
  { value: 'finalfantasy.bot.barman',     label: '🤖 Bot — Barman' },
  { value: 'finalfantasy.bot.chef',       label: '🤖 Bot — Chef' },
  { value: 'finalfantasy.bot.cashier',    label: '🤖 Bot — Cashier' },
  { value: 'finalfantasy.bot.host',       label: '🤖 Bot — Host' },
];

// ── Scope-type → signal catalog ─────────────────────────────────────────
// Each scope_type has a known set of signals the viewer can render.
// The user picks which signals to embed in the URL.

const SIGNAL_CATALOGS: Record<string, ScopeSignalItem[]> = {
  'iot-devices': [
    { key: 'temp',     label: 'Temperature',   color: '#34d399', ns: 'iot' },
    { key: 'hum',      label: 'Humidity',       color: '#60a5fa', ns: 'iot' },
    { key: 'rssi',     label: 'RSSI',           color: '#f97316', ns: 'iot' },
    { key: 'battery',  label: 'Battery',        color: '#f59e0b', ns: 'iot' },
    { key: 'power',    label: 'Power',          color: '#fb7185', ns: 'iot' },
  ],
  'daemon-ops': [
    { key: 'cpu',          label: 'CPU',          color: '#34d399', ns: 'pacdaemon.stats' },
    { key: 'memory',       label: 'Memory',       color: '#60a5fa', ns: 'pacdaemon.stats' },
    { key: 'temperature',  label: 'Temperature',  color: '#f97316', ns: 'pacdaemon.stats' },
    { key: 'disk',         label: 'Disk',         color: '#f59e0b', ns: 'pacdaemon.stats' },
    { key: 'network',      label: 'Network',      color: '#a78bfa', ns: 'pacdaemon.stats' },
    { key: 'network.ping', label: 'Ping',         color: '#e879f9', ns: 'pacdaemon.stats' },
    { key: 'power',        label: 'Power',        color: '#fb7185', ns: 'pacdaemon.stats' },
    { key: 'process',      label: 'Process',      color: '#38bdf8', ns: 'pacdaemon.stats' },
  ],
};

// ── Helpers ──────────────────────────────────────────────────────────────

function timeAgo(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const ms = Date.now() - new Date(value).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch {
    return value ?? '—';
  }
}

function linkStatus(link: SharedLinkRecord): { label: string; color: string } {
  if (link.revoked) return { label: 'Revoked', color: 'text-red-400' };
  if (link.expires_at && new Date(link.expires_at) < new Date()) {
    return { label: 'Expired', color: 'text-amber-400' };
  }
  return { label: 'Active', color: 'text-emerald-400' };
}

function scopeLabel(link: SharedLinkRecord): string {
  if (link.scope_keys && link.scope_keys.length > 1) {
    return `${link.scope_ns} · ${link.scope_keys.length} keys`;
  }
  return `${link.scope_ns}:${link.scope_key}`;
}

const SCOPE_TYPE_ICONS: Record<string, string> = {
  'table-ops':   '🎮',
  'iot-devices': '📡',
  'generic':     '📦',
};

// ── Component ────────────────────────────────────────────────────────────

export default function SharedLinksAdminPanel() {
  const queryClient = useQueryClient();
  const { tenants, activeTenantId, isSuperadmin } = useTenantOverride();
  const [expandedToken, setExpandedToken] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [copied, setCopied] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);

  // ── Generator state ─────────────────────────────────────────────────
  const [scopeType, setScopeType] = useState<string>('table-ops');
  const [scopeNs, setScopeNs]     = useState('sim.tableops');
  const [scopeKey, setScopeKey]   = useState('');
  const [label, setLabel]         = useState('');
  const [ttlHours, setTtlHours]   = useState(0);
  const [language, setLanguage]   = useState('');
  const [phantomRole, setPhantomRole] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [genCopied, setGenCopied] = useState(false);
  const [dspEnabled, setDspEnabled] = useState(false);
  const [accessLevel, setAccessLevel] = useState<SharedAccessLevel>('public');
  const [allowedUserIds, setAllowedUserIds] = useState('');

  // ── Signal selection (scope-family selector) ────────────────────────
  const signalCatalog = useMemo(() => SIGNAL_CATALOGS[scopeType] ?? [], [scopeType]);
  const signalKeys = useMemo(() => signalCatalog.map(s => s.key), [signalCatalog]);
  const { selected: selectedSignals, toggle: toggleSignal, toggleAll: toggleAllSignals, setSelected: setSelectedSignals } =
    useScopeSignalSelection(signalKeys);
  // Reset signal selection when scope type changes
  useEffect(() => {
    setSelectedSignals(new Set(signalKeys));
  }, [scopeType, signalKeys, setSelectedSignals]);

  // Active tenant display
  const activeTenant = tenants.find(t => t.idTenant === activeTenantId);
  const tenantLabel = activeTenant
    ? `${activeTenant.tenantName}`
    : activeTenantId
      ? 'Selected tenant'
      : 'No tenant selected';

  // Auto-select active tenant code for TableOps on mount / tenant change
  useEffect(() => {
    if (scopeType === 'table-ops' && activeTenant && !scopeKey) {
      setScopeKey(activeTenant.tenantName || '');
    }
  }, [scopeType, activeTenant, scopeKey]);

  // Sync scope_ns + scope_key when scope type changes
  const handleScopeTypeChange = (val: string) => {
    setScopeType(val);
    const preset = SCOPE_PRESETS.find(p => p.value === val);
    if (preset) setScopeNs(preset.scope_ns);
    // Auto-select active tenant code for TableOps
    if (val === 'table-ops' && activeTenant) {
      setScopeKey(activeTenant.tenantName || '');
    } else {
      setScopeKey('');
    }
    setGeneratedUrl(null);
  };

  // ── IoT device catalog (drives the Device ID select when iot-devices scope) ──
  const { data: allIotDevices = [] } = useDevicesList({
    enabled: scopeType === 'iot-devices',
  });
  const iotDevices = useMemo(
    () => allIotDevices.filter(d => d.tenant_id === activeTenantId && d.is_active),
    [allIotDevices, activeTenantId],
  );

  // ── Data Queries ─────────────────────────────────────────────────────
  const { data: linksData, isLoading: loadingLinks } = useQuery({
    queryKey: KEYS.list,
    queryFn: listSharedLinks,
    refetchInterval: 15_000,
  });

  const { data: demoLink } = useQuery({
    queryKey: KEYS.demo,
    queryFn: getDemoLink,
  });

  const links = useMemo(() => linksData?.items ?? [], [linksData]);

  // ── Filtered links ──────────────────────────────────────────────────
  const filteredLinks = useMemo(() => {
    if (!filter) return links;
    const lc = filter.toLowerCase();
    return links.filter(l =>
      (l.label ?? '').toLowerCase().includes(lc) ||
      l.scope_type.toLowerCase().includes(lc) ||
      l.scope_ns.toLowerCase().includes(lc) ||
      l.scope_key.toLowerCase().includes(lc),
    );
  }, [links, filter]);

  // ── Stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active  = links.filter(l => !l.revoked && (!l.expires_at || new Date(l.expires_at) >= new Date())).length;
    const total   = links.reduce((s, l) => s + l.access_count, 0);
    const unique  = links.reduce((s, l) => s + l.unique_visitors, 0);
    return { active, totalHits: total, uniqueVisitors: unique, total: links.length };
  }, [links]);

  // ── Create mutation ─────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: () => {
      const viewerConfig: ViewerConfig = {};
      if (language) viewerConfig.language = language;
      if (phantomRole) viewerConfig.phantom_role = phantomRole;
      if (dspEnabled) viewerConfig.dsp_enabled = true;
      // Embed signal selection if user deselected any
      if (signalCatalog.length > 0 && selectedSignals.size < signalCatalog.length) {
        viewerConfig.signals = Array.from(selectedSignals);
      }

      // For table-ops links: resolve the selected tenant code → tenant id
      // so the shared-link record stores the correct tenant that owns the data.
      let tenantIdOverride: number | undefined;
      if (scopeType === 'table-ops' && scopeKey) {
        const selectedTenant = tenants.find(t => t.tenantName === scopeKey);
        if (selectedTenant) tenantIdOverride = selectedTenant.idTenant;
      }

      // Parse allowed user IDs for selected_users mode
      const parsedUserIds = accessLevel === 'selected_users' && allowedUserIds.trim()
        ? allowedUserIds.split(',').map(s => Number(s.trim())).filter(n => n > 0)
        : undefined;

      return createSharedLink({
        scope_type:       scopeType,
        scope_ns:         scopeNs,
        scope_key:        scopeKey || undefined,
        label:            label || undefined,
        ttl_hours:        ttlHours || undefined,
        tenant_id:        tenantIdOverride,
        access_level:     accessLevel !== 'public' ? accessLevel : undefined,
        allowed_user_ids: parsedUserIds,
        viewer_config:    Object.keys(viewerConfig).length ? viewerConfig : undefined,
      });
    },
    onSuccess: (record) => {
      // Always build URL from browser origin — BFF origin may differ (proxy, port mismatch)
      setGeneratedUrl(`${window.location.origin}/s/${record.token}`);
      setGenCopied(false);
      queryClient.invalidateQueries({ queryKey: KEYS.list });
    },
  });

  // ── Revoke mutation ─────────────────────────────────────────────────
  const revokeMutation = useMutation({
    mutationFn: (token: string) => revokeSharedLink(token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.list }),
  });

  // ── Copy URL ────────────────────────────────────────────────────────
  const handleCopy = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenCopy = async () => {
    if (!generatedUrl) return;
    await navigator.clipboard.writeText(generatedUrl);
    setGenCopied(true);
    setTimeout(() => setGenCopied(false), 2000);
  };

  const demoUrl = demoLink ? `${window.location.origin}/s/${demoLink.token}` : null;

  const canGenerate = scopeNs.length > 0 && (scopeType !== 'generic' || scopeKey.length > 0);

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">

      {/* ── Generate New Link — Collapsible Panel ───────────────────── */}
      <div className="pac-glass-raised rounded-lg border border-emerald-500/20 shrink-0">
        <button
          onClick={() => setShowGenerator(!showGenerator)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <div className="flex items-center gap-3">
            <span className="text-base">🔗</span>
            <div>
              <div className="text-sm font-medium text-neutral-200">Generate Shared Link</div>
              <div className="text-[10px] text-neutral-500">
                Create a zero-trust URL for TableOps, IoT, or any scope — with viewer configuration
              </div>
            </div>
          </div>
          <span className={`text-neutral-500 text-xs transition-transform ${showGenerator ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {showGenerator && (
          <div className="px-4 pb-4 border-t border-neutral-800 pt-3 space-y-4">
            {/* Tenant context indicator */}
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-neutral-500 uppercase tracking-wider">Target Tenant:</span>
              <span className={`font-medium ${isSuperadmin ? 'text-orange-400' : 'text-neutral-300'}`}>
                {tenantLabel}
              </span>
              {isSuperadmin && (
                <span className="text-neutral-600">(change via sidebar tenant selector)</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ── Left: Scope Configuration ────────────────────────── */}
              <div className="space-y-3">
                {/* Scope Type */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Scope Type</label>
                  <select
                    value={scopeType}
                    onChange={e => handleScopeTypeChange(e.target.value)}
                    className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                  >
                    {SCOPE_PRESETS.map(p => (
                      <option key={p.value} value={p.value} className="bg-neutral-900">{p.label}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-neutral-600">
                    {SCOPE_PRESETS.find(p => p.value === scopeType)?.description}
                  </div>
                </div>

                {/* Scope Namespace (editable for generic) */}
                {scopeType === 'generic' && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Scope Namespace</label>
                    <input
                      type="text"
                      value={scopeNs}
                      onChange={e => { setScopeNs(e.target.value); setGeneratedUrl(null); }}
                      placeholder="e.g. sim.tableops, iot, workflow…"
                      className="w-full px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
                    />
                  </div>
                )}

                {/* Scope Key */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">
                    {scopeType === 'table-ops' ? 'Simulation (Tenant)' : scopeType === 'iot-devices' ? 'Device ID (or leave blank for all)' : 'Scope Key'}
                  </label>
                  {scopeType === 'table-ops' ? (
                    <>
                      <select
                        value={scopeKey}
                        onChange={e => { setScopeKey(e.target.value); setGeneratedUrl(null); }}
                        className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                      >
                        <option value="" disabled className="bg-neutral-900">Select a tenant…</option>
                        {tenants.map(t => (
                          <option key={t.idTenant} value={t.tenantName} className="bg-neutral-900">
                            {t.tenantName || 'Tenant'}
                          </option>
                        ))}
                      </select>
                      <div className="text-[10px] text-neutral-600">
                        Viewer sees all tables, orders, sessions for this tenant's simulation.
                      </div>
                    </>
                  ) : scopeType === 'iot-devices' ? (
                    <>
                      <select
                        value={scopeKey}
                        onChange={e => { setScopeKey(e.target.value); setGeneratedUrl(null); }}
                        className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                      >
                        <option value="" className="bg-neutral-900">All devices (tenant-wide)</option>
                        {iotDevices.length === 0 && (
                          <option disabled className="bg-neutral-900 text-neutral-600">No active devices found…</option>
                        )}
                        {iotDevices.map(d => (
                          <option key={d.device_id} value={d.device_id} className="bg-neutral-900">
                            {d.label ? `${d.label} · ${d.device_id}` : d.device_id}
                          </option>
                        ))}
                      </select>
                      <div className="text-[10px] text-neutral-600">
                        Select a specific device or leave blank to share all tenant devices.
                      </div>
                    </>
                  ) : (
                    <input
                      type="text"
                      value={scopeKey}
                      onChange={e => { setScopeKey(e.target.value); setGeneratedUrl(null); }}
                      placeholder="e.g. my-entity-key"
                      className="w-full px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
                    />
                  )}
                </div>

                {/* Label */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Label (optional)</label>
                  <input
                    type="text"
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    placeholder="e.g. Production Floor Monitor"
                    className="w-full px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
                  />
                </div>
              </div>

              {/* ── Right: Viewer Config + TTL ───────────────────────── */}
              <div className="space-y-3">
                {/* Viewer Language */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Viewer Language</label>
                  <select
                    value={language}
                    onChange={e => { setLanguage(e.target.value); setGeneratedUrl(null); }}
                    className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                  >
                    {LANGUAGE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-neutral-600">
                    Sets the display language for the external viewer UI.
                  </div>
                </div>

                {/* Phantom Role */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Phantom Role</label>
                  <select
                    value={phantomRole}
                    onChange={e => { setPhantomRole(e.target.value); setGeneratedUrl(null); }}
                    className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                  >
                    {PHANTOM_ROLES.map(o => (
                      <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-neutral-600">
                    Simulated identity the viewer operates as. Controls what the viewer sees.
                  </div>
                </div>

                {/* DSP Toggle */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Signal Processing (DSP)</label>
                  <button
                    onClick={() => { setDspEnabled(v => !v); setGeneratedUrl(null); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors
                      ${dspEnabled
                        ? 'pac-glass-raised border border-emerald-500/30 text-emerald-300'
                        : 'pac-glass border border-neutral-700/50 text-neutral-400 hover:border-neutral-600'}`}
                  >
                    <span
                      className={`relative inline-flex items-center h-4 w-7 shrink-0 rounded-full border transition-colors
                        ${dspEnabled
                          ? 'bg-emerald-600/40 border-emerald-500/60'
                          : 'bg-white/[0.04] border-white/[0.10]'}`}
                    >
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full transition-transform
                          ${dspEnabled
                            ? 'translate-x-[13px] bg-emerald-400'
                            : 'translate-x-[2px] bg-neutral-500'}`}
                      />
                    </span>
                    <span>{dspEnabled ? 'DSP Enabled' : 'DSP Off'}</span>
                  </button>
                  <div className="text-[10px] text-neutral-600">
                    Apply glitch gate, EMA smoothing, interpolation &amp; dead-zone to telemetry plots.
                  </div>
                </div>

                {/* Signal Selector (scope-family) */}
                {signalCatalog.length > 0 && (
                  <ScopeSignalSelector
                    items={signalCatalog}
                    selected={selectedSignals}
                    onToggle={toggleSignal}
                    onToggleAll={toggleAllSignals}
                    title={`${scopeType === 'iot-devices' ? 'IoT' : 'Daemon'} Signals`}
                    maxHeight={160}
                  />
                )}

                {/* TTL */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Link Expiry</label>
                  <select
                    value={ttlHours}
                    onChange={e => setTtlHours(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                  >
                    {TTL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
                    ))}
                  </select>
                </div>

                {/* Access Level */}
                <div className="space-y-1">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Access Level</label>
                  <div className="flex gap-1">
                    {(['public', 'tenant_users', 'selected_users'] as SharedAccessLevel[]).map(lvl => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => setAccessLevel(lvl)}
                        className={`flex-1 px-2 py-1.5 rounded text-[11px] border transition-colors ${
                          accessLevel === lvl
                            ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
                            : 'border-neutral-700/50 bg-transparent text-neutral-500 hover:text-neutral-300'
                        }`}
                      >
                        {lvl === 'public' ? '🌐 Public' : lvl === 'tenant_users' ? '🏢 Tenant' : '👤 Selected'}
                      </button>
                    ))}
                  </div>
                  <p className="text-[9px] text-neutral-600">
                    {accessLevel === 'public' && 'Anyone with the link can view — no sign-in required.'}
                    {accessLevel === 'tenant_users' && 'Only authenticated users from the same tenant can view.'}
                    {accessLevel === 'selected_users' && 'Only specific users (by ID) can view.'}
                  </p>
                </div>

                {/* Allowed User IDs — only for selected_users */}
                {accessLevel === 'selected_users' && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Allowed User IDs</label>
                    <input
                      type="text"
                      value={allowedUserIds}
                      onChange={e => setAllowedUserIds(e.target.value)}
                      placeholder="e.g. 12, 34, 56"
                      className="w-full px-3 py-2 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
                    />
                    <p className="text-[9px] text-neutral-600">Comma-separated numeric user IDs.</p>
                  </div>
                )}

                {/* Summary preview */}
                <div className="pac-glass rounded-lg border border-neutral-700/50 p-3 space-y-1.5 text-[10px]">
                  <div className="text-neutral-500 uppercase tracking-wider">Link Preview</div>
                  <div className="space-y-1 text-neutral-400">
                    <div><span className="text-neutral-600">Type:</span> {SCOPE_PRESETS.find(p => p.value === scopeType)?.label}</div>
                    <div><span className="text-neutral-600">Namespace:</span> {scopeNs || '—'}</div>
                    <div><span className="text-neutral-600">Key:</span> {scopeKey || '(all)'}</div>
                    <div><span className="text-neutral-600">Tenant:</span> {tenantLabel}</div>
                    <div><span className="text-neutral-600">Access:</span> {accessLevel === 'public' ? '🌐 Public' : accessLevel === 'tenant_users' ? '🏢 Tenant Users' : '👤 Selected Users'}</div>
                    {language && <div><span className="text-neutral-600">Language:</span> {LANGUAGE_OPTIONS.find(o => o.value === language)?.label}</div>}
                    {phantomRole && <div><span className="text-neutral-600">Phantom:</span> {PHANTOM_ROLES.find(o => o.value === phantomRole)?.label}</div>}
                    <div><span className="text-neutral-600">DSP:</span> {dspEnabled ? '✓ Enabled' : 'Off'}</div>
                    {signalCatalog.length > 0 && (
                      <div><span className="text-neutral-600">Signals:</span> {selectedSignals.size === signalCatalog.length ? 'All' : `${selectedSignals.size}/${signalCatalog.length}`}</div>
                    )}
                    <div><span className="text-neutral-600">Expiry:</span> {TTL_OPTIONS.find(o => o.value === ttlHours)?.label}</div>
                  </div>
                </div>

                {/* Generate button */}
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={!canGenerate || createMutation.isPending}
                  className="w-full px-4 py-2.5 rounded text-sm font-medium text-white
                             bg-emerald-600/80 hover:bg-emerald-500/80 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors"
                >
                  {createMutation.isPending ? 'Generating…' : 'Generate Shared URL'}
                </button>

                {createMutation.isError && (
                  <div className="text-red-400 text-xs">
                    Failed: {(createMutation.error as Error).message}
                  </div>
                )}
              </div>
            </div>

            {/* Generated URL output */}
            {generatedUrl && (
              <div className="pac-glass-raised rounded-lg border border-emerald-500/30 p-4 space-y-2">
                <div className="text-[10px] text-emerald-400 uppercase tracking-wider">Share URL</div>
                <div className="text-xs text-neutral-300 font-mono break-all select-all leading-relaxed">
                  {generatedUrl}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleGenCopy}
                    className="px-3 py-1.5 rounded border border-emerald-500/30 text-xs text-emerald-400
                               hover:border-emerald-400/50 transition-colors"
                  >
                    {genCopied ? '✓ Copied!' : 'Copy to Clipboard'}
                  </button>
                  <a
                    href={generatedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded border border-blue-500/30 text-xs text-blue-400
                               hover:border-blue-400/50 transition-colors"
                  >
                    Open Viewer ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FF Demo Discovery Banner ────────────────────────────────── */}
      {demoLink && demoUrl && (
        <div className="pac-glass-raised rounded-lg border border-amber-500/30 p-4 shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg">🎮</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-300">{demoLink.label}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                Omnipotent guest viewer — TableOps Big Brother · {demoLink.access_count} hits · {demoLink.unique_visitors} visitors
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded text-xs font-medium text-amber-300 border border-amber-500/30
                           hover:border-amber-400/50 transition-colors"
              >
                Open Viewer ↗
              </a>
              <button
                onClick={() => handleCopy(demoUrl)}
                className="px-3 py-1.5 rounded text-xs text-neutral-400 border border-neutral-700
                           hover:border-neutral-500 hover:text-neutral-200 transition-colors"
              >
                {copied ? '✓ Copied' : 'Copy URL'}
              </button>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-neutral-600 font-mono break-all select-all leading-relaxed">
            {demoUrl}
          </div>
        </div>
      )}

      {/* ── Summary Stats Bar ───────────────────────────────────────── */}
      <div className="flex gap-3 shrink-0 flex-wrap">
        <StatCard label="Total Links" value={stats.total} />
        <StatCard label="Active" value={stats.active} accent="emerald" />
        <StatCard label="Total Hits" value={stats.totalHits} accent="blue" />
        <StatCard label="Unique Visitors" value={stats.uniqueVisitors} accent="violet" />
      </div>

      {/* ── Filter ──────────────────────────────────────────────────── */}
      <div className="shrink-0">
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by label, scope type, namespace…"
          className="w-full max-w-md px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
        />
      </div>

      {/* ── Links Table ─────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1">
        {loadingLinks ? (
          <div className="text-neutral-500 text-sm py-8 text-center">Loading shared links…</div>
        ) : filteredLinks.length === 0 ? (
          <div className="text-neutral-500 text-sm py-8 text-center">
            {filter ? 'No links match filter.' : 'No shared links created yet.'}
          </div>
        ) : (
          filteredLinks.map(link => (
            <LinkCard
              key={link.token}
              link={link}
              isExpanded={expandedToken === link.token}
              onToggleExpand={() => setExpandedToken(expandedToken === link.token ? null : link.token)}
              onRevoke={() => revokeMutation.mutate(link.token)}
              onCopyUrl={handleCopy}
              revoking={revokeMutation.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────────

function StatCard({ label, value, accent }: { label: string; value: number; accent?: string }) {
  const color = accent
    ? `text-${accent}-400`
    : 'text-neutral-200';
  return (
    <div className="pac-glass rounded-lg border border-neutral-700/50 px-4 py-2 min-w-[100px]">
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
    </div>
  );
}

// ── Link Card ────────────────────────────────────────────────────────────

function LinkCard({
  link,
  isExpanded,
  onToggleExpand,
  onRevoke,
  onCopyUrl,
  revoking,
}: {
  link: SharedLinkRecord;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRevoke: () => void;
  onCopyUrl: (url: string) => void;
  revoking: boolean;
}) {
  const status = linkStatus(link);
  const icon   = SCOPE_TYPE_ICONS[link.scope_type] ?? '📦';
  const url    = `${window.location.origin}/s/${link.token}`;

  return (
    <div className={`pac-glass rounded-lg border transition-colors ${
      isExpanded ? 'border-neutral-600' : 'border-neutral-700/50 hover:border-neutral-700'
    }`}>
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={onToggleExpand}
      >
        <span className="text-base shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-neutral-200 truncate">
            {link.label || scopeLabel(link)}
          </div>
          <div className="text-[10px] text-neutral-600 flex items-center gap-2 mt-0.5">
            <span className="font-mono">{link.scope_type}</span>
            <span>·</span>
            <span>{scopeLabel(link)}</span>
            <span>·</span>
            <span className={status.color}>{status.label}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0 text-right">
          <div className="space-y-0.5">
            <div className="text-sm font-semibold text-neutral-200">{link.access_count}</div>
            <div className="text-[10px] text-neutral-600">hits</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-sm font-semibold text-violet-400">{link.unique_visitors}</div>
            <div className="text-[10px] text-neutral-600">visitors</div>
          </div>
          <div className="space-y-0.5">
            <div className="text-xs text-neutral-400">{timeAgo(link.last_accessed_at)}</div>
            <div className="text-[10px] text-neutral-600">last hit</div>
          </div>
          <span className={`text-neutral-600 text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <LinkDetail
          link={link}
          url={url}
          onRevoke={onRevoke}
          onCopyUrl={onCopyUrl}
          revoking={revoking}
        />
      )}
    </div>
  );
}

// ── Link Detail (expanded) ───────────────────────────────────────────────

function LinkDetail({
  link,
  url,
  onRevoke,
  onCopyUrl,
  revoking,
}: {
  link: SharedLinkRecord;
  url: string;
  onRevoke: () => void;
  onCopyUrl: (url: string) => void;
  revoking: boolean;
}) {
  // Fetch visitor details when expanded
  const { data: visitorData, isLoading: loadingVisitors } = useQuery({
    queryKey: KEYS.visitors(link.token),
    queryFn: () => getSharedLinkVisitors(link.token),
    refetchInterval: 10_000,
  });

  return (
    <div className="px-4 pb-4 border-t border-neutral-800 pt-3 space-y-3">
      {/* URL + actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] text-neutral-600 font-mono break-all flex-1 select-all leading-relaxed">
          {url}
        </div>
        <button
          onClick={() => onCopyUrl(url)}
          className="px-3 py-1 rounded text-[10px] text-neutral-400 border border-neutral-700
                     hover:border-neutral-500 hover:text-neutral-200 transition-colors shrink-0"
        >
          Copy URL
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1 rounded text-[10px] text-blue-400 border border-blue-500/30
                     hover:border-blue-400/50 transition-colors shrink-0"
        >
          Open ↗
        </a>
        {!link.revoked && (
          <button
            onClick={onRevoke}
            disabled={revoking}
            className="px-3 py-1 rounded text-[10px] text-red-400/70 border border-red-500/20
                       hover:border-red-400/40 hover:text-red-400 transition-colors shrink-0
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Revoke
          </button>
        )}
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
        <MetaCell label="Created By" value={link.created_by} />
        <MetaCell label="Created" value={timeAgo(link.created_at)} />
        <MetaCell label="Expires" value={link.expires_at ? timeAgo(link.expires_at) : 'Never'} />
        <MetaCell label="Permissions" value={link.permissions.join(', ')} />
        {link.scope_keys && link.scope_keys.length > 1 && (
          <div className="col-span-full">
            <span className="text-neutral-600 uppercase tracking-wider">Pinned Keys: </span>
            <span className="text-neutral-400 font-mono">{link.scope_keys.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Visitor Forensics */}
      <div className="space-y-2">
        <div className="text-[10px] text-neutral-500 uppercase tracking-wider flex items-center gap-2">
          Visitor Forensics
          {visitorData && (
            <span className="text-neutral-600 normal-case tracking-normal">
              ({visitorData.unique} unique · {visitorData.total_hits} total hits)
            </span>
          )}
        </div>

        {loadingVisitors ? (
          <div className="text-neutral-600 text-[10px] py-2">Loading visitor data…</div>
        ) : !visitorData || visitorData.visitors.length === 0 ? (
          <div className="text-neutral-600 text-[10px] py-2">No visitor activity recorded yet.</div>
        ) : (
          <div className="max-h-48 overflow-y-auto rounded border border-neutral-800">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-neutral-900/95 backdrop-blur">
                <tr className="text-neutral-600 uppercase tracking-wider">
                  <th className="text-left px-2 py-1.5 font-medium">Fingerprint</th>
                  <th className="text-left px-2 py-1.5 font-medium">Device</th>
                  <th className="text-left px-2 py-1.5 font-medium">Location</th>
                  <th className="text-left px-2 py-1.5 font-medium">Language</th>
                  <th className="text-left px-2 py-1.5 font-medium">Referer</th>
                  <th className="text-right px-2 py-1.5 font-medium">Time</th>
                  <th className="text-center px-2 py-1.5 font-medium">New</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800/50">
                {visitorData.visitors.map((v, i) => (
                  <VisitorRow key={`${v.fingerprint}-${i}`} visitor={v} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Visitor Row ──────────────────────────────────────────────────────────

function VisitorRow({ visitor }: { visitor: SharedLinkVisitor }) {
  const location = [visitor.city, visitor.region, visitor.country]
    .filter(Boolean)
    .join(', ') || '—';

  return (
    <tr className="hover:bg-neutral-800/30 transition-colors">
      <td className="px-2 py-1.5 font-mono text-neutral-400">{visitor.fingerprint.slice(0, 12)}…</td>
      <td className="px-2 py-1.5 text-neutral-400">
        <DeviceIcon deviceClass={visitor.device_class} />
        {' '}{visitor.device_class || '—'}
      </td>
      <td className="px-2 py-1.5 text-neutral-400">{location}</td>
      <td className="px-2 py-1.5 text-neutral-500">{visitor.language?.slice(0, 10) || '—'}</td>
      <td className="px-2 py-1.5 text-neutral-600 truncate max-w-[120px]">{visitor.referer || '—'}</td>
      <td className="px-2 py-1.5 text-neutral-500 text-right whitespace-nowrap">{timeAgo(visitor.accessed_at)}</td>
      <td className="px-2 py-1.5 text-center">
        {visitor.is_new && <span className="text-emerald-400">●</span>}
      </td>
    </tr>
  );
}

// ── Device Icon ──────────────────────────────────────────────────────────

function DeviceIcon({ deviceClass }: { deviceClass: string | null }) {
  switch (deviceClass) {
    case 'desktop': return <span>🖥</span>;
    case 'mobile':  return <span>📱</span>;
    case 'tablet':  return <span>📱</span>;
    case 'bot':     return <span>🤖</span>;
    default:        return <span>❓</span>;
  }
}

// ── Metadata Cell ────────────────────────────────────────────────────────

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-neutral-600 uppercase tracking-wider">{label}</div>
      <div className="text-neutral-400 mt-0.5">{value}</div>
    </div>
  );
}
